import { z } from 'zod';
import type { StatutCandidature } from './candidature.js';
import type { Texte } from './chartes.js';
import type { GroupeBareme } from './familles.js';

/** Types acceptés par la contrainte `justificatifs_type_connu` (migration 007). */
export const TYPES_JUSTIFICATIF = [
  'cin',
  'b3',
  'attestation',
  'diplome',
  'permis',
  'medical',
  'rib',
  'domicile',
] as const;
export type TypeJustificatif = (typeof TYPES_JUSTIFICATIF)[number];

export const LIBELLES_JUSTIFICATIF: Record<TypeJustificatif, Texte> = {
  cin: { fr: 'Copie de la carte d’identité nationale', ar: 'نسخة من بطاقة التعريف الوطنية' },
  b3: { fr: 'Extrait du bulletin n° 3', ar: 'مضمون السوابق العدلية (البطاقة عدد 3)' },
  attestation: { fr: 'Attestations de travail', ar: 'شهادات العمل' },
  diplome: { fr: 'Diplômes', ar: 'الشهائد العلمية' },
  permis: { fr: 'Permis de conduire', ar: 'رخصة السياقة' },
  // Seul le certificat est conservé : aucune donnée de santé n'est saisie.
  medical: { fr: 'Certificat d’aptitude médicale', ar: 'شهادة طبية في القدرة على العمل' },
  rib: { fr: 'Relevé d’identité bancaire', ar: 'بطاقة التعريف البنكي' },
  domicile: { fr: 'Justificatif de domicile', ar: 'شهادة إقامة' },
};

/**
 * Délai par défaut (paramètre `justificatifs.delai.jours`) : sept jours
 * calendaires à compter de la signature de la charte, comme l'écrit la charte.
 */
export const DELAI_JUSTIFICATIFS_JOURS = 7;

/**
 * Pièces demandées par défaut, ajustables candidat par candidat : identité,
 * bulletin n° 3 et attestations pour tous, diplômes facultatifs, permis pour
 * les chauffeurs. Le certificat d'aptitude médicale n'est pas demandé d'office.
 */
export function justificatifsParDefaut(
  groupe: GroupeBareme,
): Array<{ type: TypeJustificatif; obligatoire: boolean }> {
  const liste: Array<{ type: TypeJustificatif; obligatoire: boolean }> = [
    { type: 'cin', obligatoire: true },
    { type: 'b3', obligatoire: true },
    { type: 'attestation', obligatoire: true },
    { type: 'diplome', obligatoire: false },
  ];
  if (groupe === 'chauffeur') liste.push({ type: 'permis', obligatoire: true });
  return liste;
}

function jourUtc(jour: string): number {
  const [annee, mois, date] = jour.split('-').map(Number);
  return Date.UTC(annee!, mois! - 1, date!);
}

/** Jour calendaire `AAAA-MM-JJ` décalé de `jours`, sans dépendre du fuseau. */
export function ajouterJours(jour: string, jours: number): string {
  return new Date(jourUtc(jour) + jours * 86_400_000).toISOString().slice(0, 10);
}

export function ecartJours(depuis: string, jusqua: string): number {
  return Math.round((jourUtc(jusqua) - jourUtc(depuis)) / 86_400_000);
}

export type EtatJustificatif = 'recu' | 'attendu' | 'en_retard' | 'sans_echeance';

/** L'échéance est incluse : une pièce remise le jour même n'est pas en retard. */
export function etatJustificatif(
  justificatif: { recuLe: string | null; attenduAvant: string | null },
  aujourdhui: string,
): { etat: EtatJustificatif; joursRestants: number | null } {
  if (justificatif.recuLe) return { etat: 'recu', joursRestants: null };
  if (!justificatif.attenduAvant) return { etat: 'sans_echeance', joursRestants: null };
  const restants = ecartJours(aujourdhui, justificatif.attenduAvant);
  return { etat: restants < 0 ? 'en_retard' : 'attendu', joursRestants: restants };
}

export interface JustificatifDetail {
  id: string;
  type: TypeJustificatif;
  libelle: string;
  obligatoire: boolean;
  attenduAvant: string | null;
  recuLe: string | null;
  documentId: string | null;
  etat: EtatJustificatif;
  joursRestants: number | null;
}

/** Corps de PUT /candidatures/:id/justificatifs : la liste attendue pour ce candidat. */
export const definitionJustificatifsSchema = z
  .object({
    justificatifs: z
      .array(z.object({ type: z.enum(TYPES_JUSTIFICATIF), obligatoire: z.boolean() }).strict())
      .max(TYPES_JUSTIFICATIF.length),
  })
  .strict()
  .refine(
    (valeur) => new Set(valeur.justificatifs.map((j) => j.type)).size === valeur.justificatifs.length,
    'Un même justificatif ne se demande qu’une fois',
  );
export type DefinitionJustificatifs = z.infer<typeof definitionJustificatifsSchema>;

const AVANT_RAPPORT: readonly StatutCandidature[] = [
  'nouveau',
  'a_contacter',
  'contacte',
  'rdv_planifie',
  'entretien_realise',
  'vivier',
];

/** Le rapport de référence signé fait avancer le dossier, sans jamais le faire reculer. */
export function statutApresRapport(actuel: StatutCandidature): StatutCandidature {
  return AVANT_RAPPORT.includes(actuel) ? 'rapport_remis' : actuel;
}

/**
 * Toutes les pièces obligatoires reçues : « justificatifs reçus », mais
 * seulement après le rapport, pour respecter l'ordre de la chaîne nominale.
 */
export function statutApresJustificatifs(
  actuel: StatutCandidature,
  obligatoiresRecus: boolean,
): StatutCandidature {
  return obligatoiresRecus && actuel === 'rapport_remis' ? 'justificatifs_recus' : actuel;
}
